import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { addDays, format, subDays } from "date-fns";
import { CalendarDays, Plus, Minus } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { DateField } from "@/components/calculator/DateField";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import { gregorianToBangla, formatBanglaDate } from "@/lib/bengaliDate";
import {
  dateDifference, shiftDate, workingDays,
  gregorianToHijri, formatHijri, type WeekendType,
} from "@/lib/calculators/dateCalculator";
import { getBdHolidays } from "@/data/bdHolidays";

type B = { en: string; bn: string };
type Lang = "en" | "bn";
type TabId = "diff" | "add" | "working";

const T = {
  h1: { en: "Date Calculator", bn: "ডেট ক্যালকুলেটর" },
  desc: {
    en: "Days between dates, add or subtract from a date, and working-day counts — with Bengali calendar (Bangla shon) and Hijri context.",
    bn: "দুই তারিখের ব্যবধান, যোগ/বিয়োগ ও কর্মদিবস গণনা — বাংলা সন ও হিজরি ক্যালেন্ডার সহ।",
  },
  tDiff: { en: "Difference", bn: "ব্যবধান" },
  tAdd: { en: "Add / Subtract", bn: "যোগ / বিয়োগ" },
  tWorking: { en: "Working days", bn: "কর্মদিবস" },

  start: { en: "Start date", bn: "শুরুর তারিখ" },
  end: { en: "End date", bn: "শেষের তারিখ" },
  includeEnd: { en: "Include end date", bn: "শেষের দিন অন্তর্ভুক্ত" },

  add: { en: "Add", bn: "যোগ" },
  sub: { en: "Subtract", bn: "বিয়োগ" },
  years: { en: "Years", bn: "বছর" },
  months: { en: "Months", bn: "মাস" },
  weeks: { en: "Weeks", bn: "সপ্তাহ" },
  days: { en: "Days", bn: "দিন" },
  result: { en: "Result", bn: "ফলাফল" },

  weekend: { en: "Weekend", bn: "সাপ্তাহিক ছুটি" },
  bdWeek: { en: "Friday–Saturday (BD)", bn: "শুক্র–শনি (BD)" },
  intlWeek: { en: "Saturday–Sunday", bn: "শনি–রবি" },
  excludeHolidays: { en: "Exclude BD public holidays", bn: "BD সরকারি ছুটি বাদ" },

  totalDays: { en: "Total days", bn: "মোট দিন" },
  workingD: { en: "Working days", bn: "কর্মদিবস" },
  weekendD: { en: "Weekend days", bn: "ছুটির দিন" },
  holidayD: { en: "Holidays excluded", bn: "ছুটি বাদ" },

  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  presets: { en: "Presets", bn: "প্রিসেট" },
  today: { en: "Today", bn: "আজ" },
  yesterday: { en: "Yesterday", bn: "গতকাল" },
  tomorrow: { en: "Tomorrow", bn: "আগামীকাল" },
  lastWeek: { en: "Last week", bn: "গত সপ্তাহ" },
  pohela: { en: "Pohela Boishakh", bn: "পহেলা বৈশাখ" },
} as const;

const FAQ: { q: B; a: B }[] = [
  {
    q: { en: "How to calculate days between dates?", bn: "দুই তারিখের মধ্যে দিন কীভাবে গণনা?" },
    a: {
      en: "Pick a start and end date in the Difference tab. The calculator subtracts the two and shows total days, weeks, months, years, hours and minutes. Toggle 'Include end date' if you count both endpoints (e.g., contract days).",
      bn: "ব্যবধান ট্যাবে শুরু ও শেষ তারিখ দিন। ক্যালকুলেটর দিন, সপ্তাহ, মাস, বছর, ঘণ্টা ও মিনিট দেখাবে। উভয় প্রান্ত গুনতে 'শেষের দিন অন্তর্ভুক্ত' চালু করুন।",
    },
  },
  {
    q: { en: "What is Bangla shon?", bn: "বাংলা সন কী?" },
    a: {
      en: "Bangla shon is the Bengali Solar calendar, which Bangladesh uses in its revised civil form starting from Pohela Boishakh on 14 April. The current Bangla year is roughly Gregorian − 593 (after April 14) or − 594 (before).",
      bn: "বাংলা সন হলো বাংলা সৌর বর্ষপঞ্জি, যা বাংলাদেশ পহেলা বৈশাখ (১৪ এপ্রিল) থেকে সংশোধিত রূপে ব্যবহার করে। বর্তমান বাংলা সন ≈ গ্রেগরীয় − ৫৯৩ (১৪ এপ্রিলের পর) অথবা − ৫৯৪ (আগে)।",
    },
  },
  {
    q: { en: "How many working days are in Bangladesh?", bn: "বাংলাদেশে সপ্তাহে কত কর্মদিবস?" },
    a: {
      en: "BD government and most banks operate Sunday–Thursday with Friday and Saturday as weekly holidays — so 5 working days per week, minus public holidays. Some private companies use Sat–Sun weekends instead.",
      bn: "BD সরকারি ও অধিকাংশ ব্যাংক রবি–বৃহস্পতি কাজ করে; শুক্র–শনি ছুটি — সপ্তাহে ৫ কর্মদিবস (সরকারি ছুটি বাদ)। কিছু বেসরকারি প্রতিষ্ঠান শনি–রবি ছুটি দেয়।",
    },
  },
  {
    q: { en: "ডেট ক্যালকুলেটর কীভাবে ব্যবহার করব?", bn: "ডেট ক্যালকুলেটর কীভাবে ব্যবহার করব?" },
    a: {
      en: "তিনটি ট্যাব: ব্যবধান, যোগ/বিয়োগ, কর্মদিবস। প্রতিটি ট্যাবে তারিখ বসান বা ক্যালেন্ডার আইকনে ক্লিক করে নির্বাচন করুন। ফলাফল সাথে সাথে নিচে দেখানো হবে।",
      bn: "তিনটি ট্যাব: ব্যবধান, যোগ/বিয়োগ, কর্মদিবস। প্রতিটি ট্যাবে তারিখ বসান বা ক্যালেন্ডার আইকনে ক্লিক করে নির্বাচন করুন। ফলাফল সাথে সাথে নিচে দেখানো হবে।",
    },
  },
  {
    q: { en: "বাংলা সন কীভাবে গণনা হয়?", bn: "বাংলা সন কীভাবে গণনা হয়?" },
    a: {
      en: "১৪ এপ্রিল থেকে নতুন বাংলা বছর শুরু হয়। সূত্র: যদি গ্রেগরীয় তারিখ ≥ ১৪ এপ্রিল, বাংলা বছর = গ্রেগরীয় − ৫৯৩, না হলে − ৫৯৪। উদাহরণ: ১৪ এপ্রিল ২০২৬ = ১ বৈশাখ ১৪৩৩।",
      bn: "১৪ এপ্রিল থেকে নতুন বাংলা বছর শুরু হয়। সূত্র: যদি গ্রেগরীয় তারিখ ≥ ১৪ এপ্রিল, বাংলা বছর = গ্রেগরীয় − ৫৯৩, না হলে − ৫৯৪। উদাহরণ: ১৪ এপ্রিল ২০২৬ = ১ বৈশাখ ১৪৩৩।",
    },
  },
  {
    q: { en: "Why might the Hijri date differ by a day?", bn: "হিজরি তারিখ ১ দিন কম-বেশি কেন?" },
    a: {
      en: "The Hijri calendar is moon-sighting based and varies by country. We use the tabular (Kuwaiti) algorithm, which can differ ±1 day from the official Bangladesh sighting. For religious observances always confirm with local authorities.",
      bn: "হিজরি ক্যালেন্ডার চাঁদ দেখার ভিত্তিতে — দেশভেদে ভিন্ন। আমরা ট্যাবুলার (কুয়েতি) অ্যালগরিদম ব্যবহার করি, যা সরকারি তারিখ থেকে ±১ দিন ভিন্ন হতে পারে। ধর্মীয় বিষয়ে স্থানীয় কর্তৃপক্ষের সাথে যাচাই করুন।",
    },
  },
  {
    q: { en: "What counts as a 'working day' here?", bn: "এখানে 'কর্মদিবস' কী বোঝায়?" },
    a: {
      en: "Any day that is not a weekend (Fri–Sat by default for BD) and not a public holiday in our preset list. You can switch to Sat–Sun weekends or turn off the holiday filter.",
      bn: "যে দিন সাপ্তাহিক ছুটি (BD তে শুক্র–শনি) নয় এবং প্রিসেট তালিকার সরকারি ছুটিও নয়। আপনি শনি–রবি ছুটিতে বদলাতে বা ছুটি ফিল্টার বন্ধ করতে পারেন।",
    },
  },
  {
    q: { en: "Are religious holidays accurate?", bn: "ধর্মীয় ছুটির তারিখ সঠিক?" },
    a: {
      en: "Eid, Shab-e-Barat, Durga Puja and similar dates depend on lunar sighting and are marked 'approximate'. Always confirm with the official NBR / Ministry of Public Administration notification before relying on them.",
      bn: "ঈদ, শব-ই-বরাত, দুর্গা পূজা ইত্যাদি চাঁদ দেখার উপর নির্ভর করে — 'আনুমানিক' হিসেবে চিহ্নিত। নির্ভরশীল সিদ্ধান্তের আগে সরকারি বিজ্ঞপ্তি দেখুন।",
    },
  },
];

export const Route = createFileRoute("/date-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Date Calculator — Difference, Add/Subtract Days",
      description:
        "Free date calculator with Bengali calendar (Bangla shon) and Hijri support. Calculate days between dates, add/subtract days, working days. ডেট ক্যালকুলেটর।",
      path: "/date-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Date Calculator", path: "/date-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: DateCalculatorPage,
});

const VALID_TABS: TabId[] = ["diff", "add", "working"];
const HASH_TO_TAB: Record<string, TabId> = { diff: "diff", add: "add", working: "working" };

function DateCalculatorPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);

  // Tab + URL hash sync
  const [tab, setTab] = useState<TabId>("diff");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const h = window.location.hash.replace("#", "");
    if (HASH_TO_TAB[h]) setTab(HASH_TO_TAB[h]);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash.replace("#", "") !== tab) {
      window.history.replaceState(null, "", `#${tab}`);
    }
  }, [tab]);

  // Common state
  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState<Date | null>(today);
  const [endDate, setEndDate] = useState<Date | null>(addDays(today, 30));
  const [includeEnd, setIncludeEnd] = useState(false);

  // Tab 2 state
  const [op, setOp] = useState<"add" | "sub">("add");
  const [yearsStr, setYearsStr] = useState("0");
  const [monthsStr, setMonthsStr] = useState("0");
  const [weeksStr, setWeeksStr] = useState("0");
  const [daysStr, setDaysStr] = useState("30");

  // Tab 3 state
  const [weekend, setWeekend] = useState<WeekendType>("fri-sat");
  const [excludeHolidays, setExcludeHolidays] = useState(true);

  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmtN = (n: number) => num(n.toLocaleString("en-US"));
  const fmtDate = (d: Date) => {
    const s = format(d, "EEE, d MMMM yyyy");
    return useBn ? toBengaliNumerals(s) : s;
  };

  // Tab 1
  const diff = useMemo(() => {
    if (!startDate || !endDate) return null;
    return dateDifference(startDate, endDate, includeEnd);
  }, [startDate, endDate, includeEnd]);

  // Tab 2
  const shifted = useMemo(() => {
    if (!startDate) return null;
    return shiftDate(startDate, {
      years: parseInt(yearsStr, 10) || 0,
      months: parseInt(monthsStr, 10) || 0,
      weeks: parseInt(weeksStr, 10) || 0,
      days: parseInt(daysStr, 10) || 0,
      operation: op,
    });
  }, [startDate, yearsStr, monthsStr, weeksStr, daysStr, op]);

  // Tab 3
  const wResult = useMemo(() => {
    if (!startDate || !endDate) return null;
    const yrs = new Set<number>([startDate.getFullYear(), endDate.getFullYear()]);
    const hols = excludeHolidays ? Array.from(yrs).flatMap(getBdHolidays) : [];
    return workingDays(startDate, endDate, weekend, hols, true);
  }, [startDate, endDate, weekend, excludeHolidays]);

  const setPreset = (key: "today" | "yesterday" | "tomorrow" | "lastWeek" | "pohela") => {
    const t = new Date();
    if (key === "today") setStartDate(t);
    else if (key === "yesterday") setStartDate(subDays(t, 1));
    else if (key === "tomorrow") setStartDate(addDays(t, 1));
    else if (key === "lastWeek") setStartDate(subDays(t, 7));
    else if (key === "pohela") {
      const yr = t.getMonth() < 3 || (t.getMonth() === 3 && t.getDate() < 14) ? t.getFullYear() : t.getFullYear() + 1;
      setStartDate(new Date(yr, 3, 14));
    }
  };

  return (
    <CalculatorLayout
      slug="date-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Calendar"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Presets + bn toggle */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("presets")}:
          </span>
          {([
            ["today", tr("today")],
            ["yesterday", tr("yesterday")],
            ["tomorrow", tr("tomorrow")],
            ["lastWeek", tr("lastWeek")],
            ["pohela", tr("pohela")],
          ] as const).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setPreset(k)}
              className={cn(
                "rounded-full border border-border bg-card px-2.5 py-1 text-xs hover:border-accent hover:text-accent",
                lang === "bn" && "font-bn",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input
            type="checkbox"
            checked={bnDigits}
            onChange={(e) => setBnDigits(e.target.checked)}
            className="h-3.5 w-3.5 accent-accent"
          />
          {tr("bnDigits")}
        </label>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="diff" className={cn(lang === "bn" && "font-bn")}>{tr("tDiff")}</TabsTrigger>
          <TabsTrigger value="add" className={cn(lang === "bn" && "font-bn")}>{tr("tAdd")}</TabsTrigger>
          <TabsTrigger value="working" className={cn(lang === "bn" && "font-bn")}>{tr("tWorking")}</TabsTrigger>
        </TabsList>

        {/* ---------- Difference ---------- */}
        <TabsContent value="diff" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <DateField label={tr("start")} value={startDate} onChange={setStartDate} fromYear={1900} toDate={new Date(2100, 11, 31)} />
            <DateField label={tr("end")} value={endDate} onChange={setEndDate} fromYear={1900} toDate={new Date(2100, 11, 31)} />
          </div>
          <label className={cn("inline-flex items-center gap-2 text-sm", lang === "bn" && "font-bn")}>
            <input type="checkbox" checked={includeEnd} onChange={(e) => setIncludeEnd(e.target.checked)} className="h-4 w-4 accent-accent" />
            {tr("includeEnd")}
          </label>

          {diff && startDate && endDate && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5 text-center">
                <p className="text-3xl font-bold tabular-nums md:text-4xl">
                  {fmtN(diff.totalDays)} <span className="text-lg text-muted-foreground">{tr("days")}</span>
                </p>
                <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                  {fmtDate(startDate)} → {fmtDate(endDate)}
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
                <Stat label={`${tr("years")}/${tr("months")}/${tr("days")}`} value={`${num(diff.ymd.years)}y ${num(diff.ymd.months)}m ${num(diff.ymd.days)}d`} />
                <Stat label={tr("weeks")} value={fmtN(diff.weeks)} />
                <Stat label={tr("months")} value={fmtN(diff.months)} />
                <Stat label="Hours" value={fmtN(diff.hours)} />
                <Stat label="Minutes" value={fmtN(diff.minutes)} />
                <Stat label="Seconds" value={fmtN(diff.minutes * 60)} />
              </div>

              <CalendarContextCard from={startDate} to={endDate} lang={lang} useBn={useBn} />
            </motion.div>
          )}
        </TabsContent>

        {/* ---------- Add / Subtract ---------- */}
        <TabsContent value="add" className="mt-6 space-y-4">
          <DateField label={tr("start")} value={startDate} onChange={setStartDate} fromYear={1900} toDate={new Date(2100, 11, 31)} />
          <div className="flex gap-2">
            {(["add", "sub"] as const).map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => setOp(o)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium",
                  op === o ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card hover:border-accent/40",
                  lang === "bn" && "font-bn",
                )}
              >
                {o === "add" ? <Plus size={12} /> : <Minus size={12} />}
                {o === "add" ? tr("add") : tr("sub")}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <NumField label={tr("years")} value={yearsStr} onChange={setYearsStr} lang={lang} useBn={useBn} />
            <NumField label={tr("months")} value={monthsStr} onChange={setMonthsStr} lang={lang} useBn={useBn} />
            <NumField label={tr("weeks")} value={weeksStr} onChange={setWeeksStr} lang={lang} useBn={useBn} />
            <NumField label={tr("days")} value={daysStr} onChange={setDaysStr} lang={lang} useBn={useBn} />
          </div>

          {shifted && startDate && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5 text-center">
                <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
                  {tr("result")}
                </p>
                <p className="mt-1 text-2xl font-bold md:text-3xl">{fmtDate(shifted)}</p>
                <p className={cn("mt-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                  {op === "add" ? tr("add") : tr("sub")} {num(yearsStr)} {tr("years")}, {num(monthsStr)} {tr("months")}, {num(weeksStr)} {tr("weeks")}, {num(daysStr)} {tr("days")}
                </p>
              </div>

              <CalendarContextCard from={startDate} to={shifted} lang={lang} useBn={useBn} singleColumn />
            </motion.div>
          )}
        </TabsContent>

        {/* ---------- Working days ---------- */}
        <TabsContent value="working" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <DateField label={tr("start")} value={startDate} onChange={setStartDate} fromYear={1900} toDate={new Date(2100, 11, 31)} />
            <DateField label={tr("end")} value={endDate} onChange={setEndDate} fromYear={1900} toDate={new Date(2100, 11, 31)} />
          </div>

          <div className="grid gap-3 rounded-lg border border-border bg-card/50 p-3 md:grid-cols-2">
            <div>
              <p className={cn("mb-1.5 text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>
                {tr("weekend")}
              </p>
              <div className="flex gap-1.5">
                {(["fri-sat", "sat-sun"] as const).map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setWeekend(w)}
                    className={cn(
                      "rounded-full border px-3 py-1 text-xs",
                      weekend === w ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card hover:border-accent/40",
                      lang === "bn" && "font-bn",
                    )}
                  >
                    {w === "fri-sat" ? tr("bdWeek") : tr("intlWeek")}
                  </button>
                ))}
              </div>
            </div>
            <label className={cn("inline-flex items-center gap-2 self-end text-sm", lang === "bn" && "font-bn")}>
              <input
                type="checkbox"
                checked={excludeHolidays}
                onChange={(e) => setExcludeHolidays(e.target.checked)}
                className="h-4 w-4 accent-accent"
              />
              {tr("excludeHolidays")}
            </label>
          </div>

          {wResult && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <div className="grid gap-3 md:grid-cols-4">
                <Stat label={tr("totalDays")} value={fmtN(wResult.totalDays)} />
                <Stat label={tr("workingD")} value={fmtN(wResult.workingDays)} highlight />
                <Stat label={tr("weekendD")} value={fmtN(wResult.weekendDays)} />
                <Stat label={tr("holidayD")} value={fmtN(wResult.holidayDays)} />
              </div>

              {wResult.excludedHolidays.length > 0 && (
                <div className="rounded-xl border border-border bg-card">
                  <div className="border-b border-border px-4 py-2.5">
                    <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>
                      {lang === "en" ? "Holidays in this range" : "এই পরিসরের ছুটি"}
                    </h3>
                  </div>
                  <ul className="divide-y divide-border text-sm">
                    {wResult.excludedHolidays.map((h) => (
                      <li key={h.date} className="flex items-center justify-between px-4 py-2">
                        <span className={cn(lang === "bn" && "font-bn")}>
                          {h.name[lang]}
                          {h.approximate && (
                            <span className="ml-1.5 text-xs text-amber-600">({lang === "en" ? "approx." : "আনুমানিক"})</span>
                          )}
                        </span>
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {useBn ? toBengaliNumerals(h.date) : h.date}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          )}
        </TabsContent>
      </Tabs>

      {/* ---------- Educational content ---------- */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "en" ? "How to calculate days between two dates" : "দুই তারিখের মধ্যে দিন গণনা"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Subtract the earlier date from the later date in days. For contracts and notices, count whether the end date is included — toggle 'Include end date' to add 1 day. The Y/M/D breakdown above counts full calendar years and months first, then leftover days."
              : "পরের তারিখ থেকে আগের তারিখ দিনে বিয়োগ করুন। চুক্তি ও নোটিশের ক্ষেত্রে শেষ দিন গণনায় ধরা হবে কি না দেখুন — 'শেষের দিন অন্তর্ভুক্ত' চালু করলে ১ দিন যোগ হয়। উপরের Y/M/D ভাগে পূর্ণ বছর ও মাস আগে, তারপর অবশিষ্ট দিন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Bengali calendar (Bangla shon)" : "বাংলা সন"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Bangladesh uses the revised Bengali Solar calendar where Pohela Boishakh falls on 14 April every year. The first five months (Boishakh–Bhadro) have 31 days, the rest have 30, with Falgun gaining a 31st day in leap years. The current Bangla year is roughly Gregorian − 593 (after April 14) or − 594 (before)."
              : "বাংলাদেশ সংশোধিত বাংলা সৌর বর্ষপঞ্জি ব্যবহার করে — পহেলা বৈশাখ প্রতি বছর ১৪ এপ্রিল। প্রথম পাঁচ মাস (বৈশাখ–ভাদ্র) ৩১ দিন, বাকিগুলো ৩০, লিপ ইয়ারে ফাল্গুন ৩১ দিন। বাংলা সন ≈ গ্রেগরীয় − ৫৯৩ (১৪ এপ্রিলের পর) বা − ৫৯৪ (আগে)।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Hijri (Islamic) calendar" : "হিজরি (ইসলামিক) ক্যালেন্ডার"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "The Hijri year has 12 lunar months (Muharram, Safar, Rabi'-al-Awwal, … Dhul-Hijjah), totalling 354–355 days. Our converter uses the tabular Kuwaiti algorithm and may differ ±1 day from the official sighting-based calendar in Bangladesh."
              : "হিজরি বছরে ১২টি চান্দ্র মাস (মুহাররম, সফর, রবিউল আউয়াল, … জিলহজ), মোট ৩৫৪–৩৫৫ দিন। আমাদের কনভার্টার ট্যাবুলার কুয়েতি অ্যালগরিদম ব্যবহার করে — সরকারি চাঁদ দেখার তারিখ থেকে ±১ দিন ভিন্ন হতে পারে।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Working days vs calendar days" : "কর্মদিবস বনাম দিনপঞ্জিকা দিন"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "BD government and most banks treat Friday and Saturday as the weekly holiday — so a typical month has ~22 working days. Many corporates and freelancers follow Sat–Sun weekends instead. Use the toggle above to match your context."
              : "BD সরকারি ও অধিকাংশ ব্যাংক শুক্র–শনি ছুটি — মাসে ~২২ কর্মদিবস। অনেক কর্পোরেট ও ফ্রিল্যান্সার শনি–রবি ছুটি অনুসরণ করে। উপরের টগল দিয়ে আপনার পরিস্থিতি মেলান।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "BD public holidays — current year" : "BD সরকারি ছুটি — চলতি বছর"} lang={lang}>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">{lang === "en" ? "Date" : "তারিখ"}</th>
                  <th className="px-3 py-2 text-left">{lang === "en" ? "Holiday" : "ছুটি"}</th>
                  <th className="px-3 py-2 text-left">{lang === "en" ? "Type" : "ধরন"}</th>
                </tr>
              </thead>
              <tbody>
                {getBdHolidays(new Date().getFullYear()).map((h) => (
                  <tr key={h.date} className="border-t border-border">
                    <td className="px-3 py-2 font-mono tabular-nums">{useBn ? toBengaliNumerals(h.date) : h.date}</td>
                    <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>
                      {h.name[lang]} {h.approximate && <span className="ml-1 text-xs text-amber-600">({lang === "en" ? "approx." : "আনুমানিক"})</span>}
                    </td>
                    <td className="px-3 py-2 capitalize text-muted-foreground">{h.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={lang === "en" ? "Common use cases" : "সাধারণ ব্যবহার"} lang={lang}>
          <ul className={cn("list-disc space-y-1.5 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Project deadlines — count working days excluding holidays." : "প্রজেক্ট ডেডলাইন — ছুটি বাদ দিয়ে কর্মদিবস গণনা।"}</li>
            <li>{lang === "en" ? "Contract / notice period — include end date for 'inclusive' counts." : "চুক্তি / নোটিশ পিরিয়ড — 'অন্তর্ভুক্ত' গণনার জন্য শেষ দিন যোগ।"}</li>
            <li>{lang === "en" ? "Leave calculation, maternity / paternity day count." : "ছুটি গণনা, মাতৃত্ব / পিতৃত্বকালীন দিন।"}</li>
            <li>{lang === "en" ? "Visa / passport validity remaining." : "ভিসা / পাসপোর্ট মেয়াদ গণনা।"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/age-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Age" : "বয়স"}</Link>
          </div>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

/* ---------------- Helpers ---------------- */

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={cn(
      "rounded-xl border p-3",
      highlight ? "border-accent bg-accent/10" : "border-border bg-card",
    )}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function NumField({
  label, value, onChange, lang, useBn,
}: { label: string; value: string; onChange: (v: string) => void; lang: Lang; useBn: boolean }) {
  const display = useBn ? toBengaliNumerals(value) : value;
  return (
    <div className="space-y-1.5">
      <label className={cn("block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{label}</label>
      <input
        type="text"
        inputMode="numeric"
        value={display}
        onChange={(e) => onChange(e.target.value.replace(/[০-৯]/g, (d) => String("০১২৩৪৫৬৭৮৯".indexOf(d))).replace(/[^0-9-]/g, ""))}
        className="w-full rounded-lg border border-border bg-muted px-3 py-2 text-base tabular-nums outline-none focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20"
      />
    </div>
  );
}

function CalendarContextCard({
  from, to, lang, useBn, singleColumn,
}: { from: Date; to: Date; lang: Lang; useBn: boolean; singleColumn?: boolean }) {
  const fromBangla = gregorianToBangla(from);
  const toBangla = gregorianToBangla(to);
  const fromHijri = gregorianToHijri(from);
  const toHijri = gregorianToHijri(to);

  return (
    <div className={cn("grid gap-3", !singleColumn && "md:grid-cols-2")}>
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
          <CalendarDays size={12} /> {lang === "en" ? "Bengali calendar" : "বাংলা সন"}
        </div>
        <p className={cn("text-sm", lang === "bn" && "font-bn")}>
          {lang === "en" ? "From " : "শুরু "}<strong>{formatBanglaDate(fromBangla, useBn ? "bn" : "en")}</strong>
        </p>
        <p className={cn("text-sm", lang === "bn" && "font-bn")}>
          {lang === "en" ? "To " : "শেষ "}<strong>{formatBanglaDate(toBangla, useBn ? "bn" : "en")}</strong>
        </p>
      </div>
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
          <CalendarDays size={12} /> {lang === "en" ? "Hijri calendar" : "হিজরি সন"}
        </div>
        <p className={cn("text-sm", lang === "bn" && "font-bn")}>
          {lang === "en" ? "From " : "শুরু "}<strong>{formatHijri(fromHijri, useBn ? "bn" : "en")}</strong>
        </p>
        <p className={cn("text-sm", lang === "bn" && "font-bn")}>
          {lang === "en" ? "To " : "শেষ "}<strong>{formatHijri(toHijri, useBn ? "bn" : "en")}</strong>
        </p>
        <p className="mt-1 text-[10px] text-muted-foreground">
          {lang === "en" ? "Tabular (Kuwaiti) — may differ ±1 day from sighting." : "ট্যাবুলার (কুয়েতি) — চাঁদ দেখার তারিখ থেকে ±১ দিন ভিন্ন হতে পারে।"}
        </p>
      </div>
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: Lang }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}

